import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Lock, Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [mode, setMode] = useState(token ? 'reset' : 'request'); // 'request' or 'reset'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [success, setSuccess] = useState(false);

  // Verify token on mount if present
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify-reset-token/${token}`);
      const data = await response.json();
      setTokenValid(data.valid);
      if (!data.valid) {
        toast.error(data.message || 'Invalid or expired token');
      }
    } catch (error) {
      setTokenValid(false);
      toast.error('Failed to verify token');
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Password reset email sent!');
        
        // For development: show token if returned
        if (data.dev_token) {
          console.log('Dev token:', data.dev_token);
          toast.info(`Dev mode: Check console for reset token`, { duration: 10000 });
        }
      } else {
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
        setTimeout(() => navigate('/admin'), 2000);
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E293B] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-[#1E293B] mb-4">
            {mode === 'request' ? 'Email Sent!' : 'Password Reset!'}
          </h2>
          <p className="text-[#64748B] mb-6">
            {mode === 'request' 
              ? 'Check your email for the password reset link.'
              : 'Your password has been reset successfully. Redirecting to login...'}
          </p>
          <Link to="/admin">
            <Button className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white">
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (mode === 'reset' && tokenValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E293B] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="text-red-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-[#1E293B] mb-4">Invalid or Expired Link</h2>
          <p className="text-[#64748B] mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Button 
            onClick={() => { setMode('request'); setTokenValid(null); }}
            className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white"
          >
            Request New Link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E293B] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="/munro-logo.png" 
            alt="MUNRO" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-[#1E293B]">
            {mode === 'request' ? 'Forgot Password' : 'Reset Password'}
          </h1>
          <p className="text-[#64748B] mt-2">
            {mode === 'request' 
              ? 'Enter your registered email to receive a reset link'
              : 'Enter your new password'}
          </p>
        </div>

        {mode === 'request' ? (
          <form onSubmit={handleRequestReset} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#1E293B] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                <Input
                  type="email"
                  placeholder="Enter your registered email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                  data-testid="reset-email-input"
                />
              </div>
              <p className="text-xs text-[#64748B] mt-2">
                Registered email: simpsmunro@gmail.com
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white py-6"
              data-testid="send-reset-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#1E293B] mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                  data-testid="new-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1E293B] mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || tokenValid === null}
              className="w-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white py-6"
              data-testid="reset-password-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link 
            to="/admin" 
            className="text-[#2563EB] hover:text-[#1D4ED8] text-sm font-medium inline-flex items-center"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
